import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Subscription() {
  const [loaded, setLoaded] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setLoaded(true);
    // Scroll to top on mount so navigation from /service lands at the page header,
    // not whatever scroll position the user had on the previous route.
    window.scrollTo(0, 0);
  }, []);

  const plans = {
    monthly: {
      name: 'Monthly',
      price: 25,
      interval: 'month',
      description: 'Billed monthly',
      savings: null,
    },
    annual: {
      name: 'Annual',
      price: 250,
      interval: 'year',
      description: 'Billed annually',
      savings: 'Save CHF 50 (2 months free)',
      monthlyEquivalent: 20.83,
    },
  };

  const handleSubscribe = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/.netlify/functions/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        // The backend looks up the correct Stripe Price ID from env vars
        // based on planType, so the frontend never needs to know them.
        body: JSON.stringify({
          planType: selectedPlan,
        }),
      });

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Error creating checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error connecting to payment system. Please try again.');
    }
    setIsLoading(false);
  };

  const features = [
    {
      icon: '📊',
      title: 'Weekly Trading Signals',
      description: 'Precise entry and exit signals for 11 cryptocurrency assets',
    },
    {
      icon: '🛡️',
      title: 'Risk Management',
      description: 'Statistically-validated stop-loss levels for each position',
    },
    {
      icon: '📈',
      title: 'Portfolio Tracking',
      description: 'Performance metrics, equity curves, and trade history',
    },
    {
      icon: '📝',
      title: 'Market Analysis',
      description: 'Contextual analysis combining technicals and macro outlook',
    },
    {
      icon: '⏰',
      title: 'Timely Delivery',
      description: 'Reports delivered every Monday before market opens',
    },
    {
      icon: '🔒',
      title: 'Exclusive Access',
      description: 'Personalized reports watermarked to your subscription',
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      padding: '120px 5% 80px',
    }}>
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '4rem',
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
        }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 700,
            color: '#ffffff',
            marginBottom: '1rem',
          }}>
            The Crypto Market Report
          </h1>
          <div style={{
            width: '80px',
            height: '2px',
            background: 'linear-gradient(90deg, transparent 0%, #4a9eff 50%, transparent 100%)',
            margin: '0 auto 1.5rem',
          }} />
          <p style={{
            color: '#a8c5e6',
            fontSize: '1.15rem',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.7,
          }}>
            Weekly algorithmic trading signals and market analysis, 
            delivered every Monday before the markets open.
          </p>
        </div>

        {/* New-member discount badge — only relevant for the monthly plan */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '3rem',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.8s ease-out 0.2s',
          minHeight: '52px',
        }}>
          {selectedPlan === 'monthly' ? (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, rgba(80, 227, 194, 0.15) 0%, rgba(80, 227, 194, 0.05) 100%)',
              border: '1px solid rgba(80, 227, 194, 0.3)',
              borderRadius: '30px',
            }}>
              <span style={{ fontSize: '1.25rem' }}>🎁</span>
              <span style={{
                color: '#50e3c2',
                fontWeight: 600,
                fontSize: '0.95rem',
              }}>
                50% off your first month — new monthly subscribers only
              </span>
            </div>
          ) : (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, rgba(74, 158, 255, 0.10) 0%, rgba(74, 158, 255, 0.03) 100%)',
              border: '1px solid rgba(74, 158, 255, 0.25)',
              borderRadius: '30px',
            }}>
              <span style={{ fontSize: '1.25rem' }}>★</span>
              <span style={{
                color: '#a8c5e6',
                fontWeight: 600,
                fontSize: '0.95rem',
              }}>
                Best value — save CHF 50 with the annual plan
              </span>
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          marginBottom: '4rem',
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.8s ease-out 0.3s, transform 0.8s ease-out 0.3s',
        }}>
          {/* Monthly Plan */}
          <div
            onClick={() => setSelectedPlan('monthly')}
            style={{
              background: selectedPlan === 'monthly' 
                ? 'linear-gradient(135deg, rgba(74, 158, 255, 0.1) 0%, rgba(74, 158, 255, 0.02) 100%)'
                : 'rgba(255, 255, 255, 0.02)',
              border: selectedPlan === 'monthly'
                ? '2px solid #4a9eff'
                : '1px solid rgba(74, 158, 255, 0.15)',
              borderRadius: '16px',
              padding: '2.5rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              position: 'relative',
            }}
          >
            {selectedPlan === 'monthly' && (
              <div style={{
                position: 'absolute',
                top: '-12px',
                right: '20px',
                background: '#4a9eff',
                color: '#0a1628',
                padding: '0.35rem 1rem',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Selected
              </div>
            )}
            <h3 style={{
              color: '#ffffff',
              fontSize: '1.5rem',
              fontWeight: 600,
              marginBottom: '0.5rem',
            }}>
              Monthly
            </h3>
            <p style={{
              color: '#6b9bc9',
              fontSize: '0.9rem',
              marginBottom: '1.5rem',
            }}>
              Flexible month-to-month
            </p>
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '0.25rem',
              marginBottom: '1.5rem',
            }}>
              <span style={{
                color: '#ffffff',
                fontSize: '3rem',
                fontWeight: 700,
              }}>
                CHF 25
              </span>
              <span style={{
                color: '#6b9bc9',
                fontSize: '1rem',
              }}>
                /month
              </span>
            </div>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}>
              {['50% off first month (new members)', 'Cancel anytime', 'Weekly reports'].map((item, i) => (
                <li key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#a8c5e6',
                  fontSize: '0.9rem',
                  marginBottom: '0.5rem',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4a9eff" strokeWidth="2">
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Annual Plan */}
          <div
            onClick={() => setSelectedPlan('annual')}
            style={{
              background: selectedPlan === 'annual'
                ? 'linear-gradient(135deg, rgba(80, 227, 194, 0.1) 0%, rgba(80, 227, 194, 0.02) 100%)'
                : 'rgba(255, 255, 255, 0.02)',
              border: selectedPlan === 'annual'
                ? '2px solid #50e3c2'
                : '1px solid rgba(74, 158, 255, 0.15)',
              borderRadius: '16px',
              padding: '2.5rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              position: 'relative',
            }}
          >
            <div style={{
              position: 'absolute',
              top: '-12px',
              left: '20px',
              background: 'linear-gradient(135deg, #50e3c2 0%, #2dd4bf 100%)',
              color: '#0a1628',
              padding: '0.35rem 1rem',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              Best Value
            </div>
            {selectedPlan === 'annual' && (
              <div style={{
                position: 'absolute',
                top: '-12px',
                right: '20px',
                background: '#50e3c2',
                color: '#0a1628',
                padding: '0.35rem 1rem',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Selected
              </div>
            )}
            <h3 style={{
              color: '#ffffff',
              fontSize: '1.5rem',
              fontWeight: 600,
              marginBottom: '0.5rem',
            }}>
              Annual
            </h3>
            <p style={{
              color: '#50e3c2',
              fontSize: '0.9rem',
              marginBottom: '1.5rem',
              fontWeight: 500,
            }}>
              Save CHF 50 — 2 months free!
            </p>
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '0.25rem',
              marginBottom: '0.5rem',
            }}>
              <span style={{
                color: '#ffffff',
                fontSize: '3rem',
                fontWeight: 700,
              }}>
                CHF 250
              </span>
              <span style={{
                color: '#6b9bc9',
                fontSize: '1rem',
              }}>
                /year
              </span>
            </div>
            <p style={{
              color: '#6b9bc9',
              fontSize: '0.85rem',
              marginBottom: '1.5rem',
            }}>
              That's only CHF 20.83/month
            </p>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}>
              {['Cancel anytime', 'Weekly reports', '2 months free vs monthly'].map((item, i) => (
                <li key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#a8c5e6',
                  fontSize: '0.9rem',
                  marginBottom: '0.5rem',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#50e3c2" strokeWidth="2">
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Subscribe Button */}
        <div style={{
          textAlign: 'center',
          marginBottom: '4rem',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.8s ease-out 0.4s',
        }}>
          <button
            onClick={handleSubscribe}
            disabled={isLoading}
            style={{
              padding: '1.25rem 4rem',
              background: isLoading 
                ? 'rgba(74, 158, 255, 0.5)'
                : 'linear-gradient(135deg, #4a9eff 0%, #1a6ed8 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '1.1rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 20px rgba(74, 158, 255, 0.3)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={e => {
              if (!isLoading) {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 8px 30px rgba(74, 158, 255, 0.4)';
              }
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 20px rgba(74, 158, 255, 0.3)';
            }}
          >
            {isLoading ? 'Processing...' : 'Subscribe'}
          </button>
          <p style={{
            marginTop: '1rem',
            color: '#6b9bc9',
            fontSize: '0.85rem',
          }}>
            {selectedPlan === 'monthly'
              ? 'New members are billed CHF 12.50 today (50% off), then CHF 25/month. Cancel anytime.'
              : 'Billed CHF 250 today, then annually. Cancel anytime — access continues until the end of the period.'}
          </p>
        </div>

        {/* Features Grid */}
        <div style={{
          marginBottom: '4rem',
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.8s ease-out 0.5s, transform 0.8s ease-out 0.5s',
        }}>
          <h2 style={{
            textAlign: 'center',
            color: '#ffffff',
            fontSize: '1.75rem',
            fontWeight: 600,
            marginBottom: '2.5rem',
          }}>
            What's Included
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
          }}>
            {features.map((feature, index) => (
              <div key={index} style={{
                display: 'flex',
                gap: '1rem',
                padding: '1.5rem',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(74, 158, 255, 0.1)',
                borderRadius: '12px',
              }}>
                <span style={{ fontSize: '1.5rem' }}>{feature.icon}</span>
                <div>
                  <h3 style={{
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: 600,
                    marginBottom: '0.35rem',
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    color: '#6b9bc9',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                    margin: 0,
                  }}>
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Manage Subscription */}
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(74, 158, 255, 0.1)',
          borderRadius: '12px',
          marginBottom: '3rem',
        }}>
          <h3 style={{
            color: '#ffffff',
            fontSize: '1.1rem',
            fontWeight: 600,
            marginBottom: '0.75rem',
          }}>
            Already a subscriber?
          </h3>
          <p style={{
            color: '#6b9bc9',
            fontSize: '0.95rem',
            marginBottom: '1.25rem',
          }}>
            Manage your subscription, update payment method, or cancel anytime.
          </p>
          <a
            href="https://billing.stripe.com/p/login/9B65kE7Cz0tj8TRcn58EM00"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '0.75rem 2rem',
              background: 'transparent',
              border: '1px solid #4a9eff',
              borderRadius: '6px',
              color: '#4a9eff',
              fontSize: '0.9rem',
              fontWeight: 500,
              textDecoration: 'none',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={e => {
              e.target.style.background = 'rgba(74, 158, 255, 0.1)';
            }}
            onMouseLeave={e => {
              e.target.style.background = 'transparent';
            }}
          >
            Manage Subscription
          </a>
        </div>

        {/* Legal Links */}
        <div style={{
          textAlign: 'center',
          borderTop: '1px solid rgba(74, 158, 255, 0.1)',
          paddingTop: '2rem',
        }}>
          <p style={{
            color: '#5a7fa5',
            fontSize: '0.85rem',
            marginBottom: '1rem',
          }}>
            By subscribing, you agree to our{' '}
            <Link to="/terms" style={{ color: '#4a9eff', textDecoration: 'none' }}>Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" style={{ color: '#4a9eff', textDecoration: 'none' }}>Privacy Policy</Link>.
          </p>
          <p style={{
            color: '#4a6a8a',
            fontSize: '0.8rem',
          }}>
            Secure payments powered by Stripe. No refunds. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
